import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, IconButton,
  MenuItem, Select, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CustomDatePicker from '../form-components/CustomDatePicker';
import MediaPreviewOverlay from '../common/MediaPreviewOverlay';
import LocalText from '../common/LocalText';
import {
  C, btnBase, ctaNavy,
} from '../../theme/adminTokens';
import { getItemPicker, getItemTypes } from '../../api/itemApi';
import {
  getAnnouncements, getAvailableSchedules, getPrizeCatalog, getPrizePackItems,
} from '../../api/gameAnnouncementApi';
import {
  announcementQuizzes, fmtDateOnly, formatPeriodRange, isoDate, quizSpan,
} from '../../utils/gameSchedule';
import { todayEastern } from '../../utils/dateUtils';
import PrizeItemPickerModal from './PrizeItemPickerModal';

// getItemPicker() (sp_Item_SelectPickerBySpace) excludes prize-only items — see
// migration/game-announcement.sql's note on that proc — so the picker merges it with the
// prize-only catalog (getPrizeCatalog) to offer "either a normal inventory item, or a
// prize-only item" per the feature's own requirement, not just prize-only ones. Both are
// lean, flat shapes ({ itemId, itemTypeId, name, thumbnailUrl? }) — no nested
// merchandise/ticket/badge/avatar sub-objects like the full CMS catalog has.
const catalogItemLabel = (item) => item?.name || `Item #${item?.itemId}`;

// Prize-only items (getPrizeCatalog's lean projection) resolve thumbnailUrl the same way
// getItemPicker's do — sp_Item_SelectPrizeOnlyBySpace's own OUTER APPLY on
// merchandise/ticket/badge/avatar thumbnails.
const catalogItemImage = (item) => item?.thumbnailUrl || null;

// Same thin-scrollbar treatment as AnnouncementDetailModal.jsx/PrizeItemPickerModal.jsx —
// the native OS scrollbar on this dialog's own overflow looked out of place against the
// rest of the redesigned Game Announcement + Prize Grant flow.
const thinScrollbarSx = {
  '&::-webkit-scrollbar': { width: 6, height: 6 },
  '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
  '&::-webkit-scrollbar-thumb': { backgroundColor: C.border, borderRadius: 4 },
  '&::-webkit-scrollbar-thumb:hover': { backgroundColor: C.muted },
  scrollbarWidth: 'thin',
  scrollbarColor: `${C.border} transparent`,
};

// Same three periods as ScoreboardFilterBar's FILTERS, minus "All Time" — an announcement
// is always scoped to a period (weekly/monthly/custom), there's no all-time announcement.
const ANNOUNCEMENT_TYPES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'custom', label: 'Custom' },
];

const DEFAULT_TOP_COUNT = 10;
// The winners list is one page of the ranking (page size = top count) and a page holds at most 200 players, same cap as the API.
const MAX_TOP_COUNT = 200;

// Avatars are never a grantable prize (same exclusion PackageAddItems.jsx applies for its
// own reasons) — the picker only ever offers prize-only items and normal catalog items.
const AVATAR_ITEM_TYPE_ID = 6;

// Mirrors GameAnnouncementService.ResolveDateRange's exact Weekly/Monthly formula (C#) —
// this MUST stay identical to that formula, since the resolved Start/End Date fields below
// only ever show what the server is actually going to compute, never something the admin
// can independently edit for these two types. All math done in UTC-anchored ms-since-epoch
// to avoid local-timezone/DST drift from the server's own UTC-based calculation.
const MS_PER_DAY = 86400000;
const resolvePeriod = (type, referenceDate) => {
  if (!referenceDate) return null;
  const anchor = new Date(`${referenceDate}T00:00:00Z`).getTime();

  if (type === 'weekly') {
    const epoch = Date.UTC(1900, 0, 1);
    const days = Math.floor((anchor - epoch) / MS_PER_DAY);
    const weekStartMs = epoch + Math.floor(days / 7) * 7 * MS_PER_DAY;
    return { start: new Date(weekStartMs), end: new Date(weekStartMs + 6 * MS_PER_DAY) };
  }

  if (type === 'monthly') {
    const d = new Date(anchor);
    const start = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
    // Day 0 of next month = last day of this month.
    const end = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0));
    return { start, end };
  }

  return null;
};

const toIso = (d) => d.toISOString().slice(0, 10);

// Same overlap test the backend's EnsureNoDateClashAsync uses (GameAnnouncementService.cs) —
// two ranges overlap iff each one's start is on/before the other's end.
const rangesOverlap = (aStart, aEnd, bStart, bEnd) => aStart <= bEnd && aEnd >= bStart;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

const toggleGroupSx = {
  bgcolor: '#ffffff',
  border: `1px solid ${C.border}`,
  borderRadius: '10px',
  p: '3px',
  '& .MuiToggleButtonGroup-grouped': {
    border: 0,
    borderRadius: '8px !important',
    px: 2,
    py: 0.5,
    fontSize: 12.5,
    fontWeight: 700,
    textTransform: 'none',
    color: C.muted,
    '&:hover': { bgcolor: '#eff6ff' },
    '&.Mui-selected': { ...ctaNavy },
  },
};

// The Quizzes multi-select list: a checkbox per row so it is clear what is picked, the picked rows tinted, the period under
// the name instead of after it.
const quizMenuProps = {
  PaperProps: {
    sx: {
      mt: 0.5, maxHeight: 320, borderRadius: '10px', border: `1px solid ${C.border}`,
      boxShadow: '0 8px 24px rgba(10,50,109,0.12)', '& .MuiList-root': { p: 0.5 },
    },
  },
};

const quizOptionSx = {
  borderRadius: '8px', alignItems: 'center', gap: 1, whiteSpace: 'normal', py: 0.75, px: 1,
  '& + &': { mt: '2px' },
  '&.Mui-selected': { bgcolor: '#eff6ff' },
  '&.Mui-selected:hover, &.Mui-selected.Mui-focusVisible': { bgcolor: '#dbeafe' },
};

const inputSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: 13 },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: C.navy },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: C.navy },
};

/**
 * Create/edit modal for a Game Announcement: name, type toggle (Weekly/Monthly/Custom), a
 * "which week/month" picker that drives auto-filled Start/End Date fields, an independent
 * Announcement Date (the day prizes are planned to be granted — unrelated to the scoring
 * window), top_count, and a prize pack picker. Follows ScoreboardFilterBar's
 * segmented-toggle look and ItemModal's dialog chrome.
 */
export default function AnnouncementFormModal({
  open, onClose, onSave, initialData = null, gameId = null, scheduled = false,
}) {
  const isEdit = Boolean(initialData?.id);
  const navigate = useNavigate();

  // Today is the Eastern business day (what the leaderboard and the quiz use), not the UTC date.
  const todayIso = () => todayEastern();

  const [name, setName] = useState('');
  const [announcementType, setAnnouncementType] = useState('weekly');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // Weekly/Monthly: any date inside the desired week/month — the server still computes the
  // actual boundaries, this just picks WHICH week/month. Defaults to today so an untouched
  // form keeps the original "current week/month" behavior.
  const [referenceDate, setReferenceDate] = useState(todayIso());
  // The day prizes are planned to be granted — required, but free-standing: no relation to
  // start/end date.
  const [announcementDate, setAnnouncementDate] = useState('');
  const [topCount, setTopCount] = useState(String(DEFAULT_TOP_COUNT));
  const [saving, setSaving] = useState(false);

  // Quiz: the announcement is for one or more quizzes (the approved ones no other announcement uses, plus the ones
  // this announcement already has); its type and dates come from them, so there is no period to pick.
  const [schedules, setSchedules] = useState([]);
  const [schedulesLoading, setSchedulesLoading] = useState(Boolean(scheduled && gameId));
  const [scheduleIds, setScheduleIds] = useState([]);
  const isCustom = !scheduled && announcementType === 'custom';
  // Once winners are granted the quiz set is frozen (the server refuses it too).
  const hasWinners = Boolean(initialData?.hasWinners);

  // ── Prize pack ──────────────────────────────────────────────────────────────
  const [catalog, setCatalog] = useState([]); // merged full catalog + prize-only catalog
  const [itemTypes, setItemTypes] = useState([]);
  const [prizePackItemIds, setPrizePackItemIds] = useState([]); // number[] — always qty 1
  const [pickerOpen, setPickerOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  // ── Sibling announcements (same game) — for date-clash validation/UI ────────
  // { id, name, startStr, endStr }[] — every OTHER non-deleted announcement for this game,
  // excluding the one being edited. Powers both the calendar's blocked-date rendering
  // (Custom type) and the inline clash warning (all types) — see EnsureNoDateClashAsync's
  // remarks in GameAnnouncementService.cs for why this check exists at all.
  // Not used for a Quiz: its announcements may overlap (each quiz belongs to one announcement only).
  const [siblingRanges, setSiblingRanges] = useState([]);

  const loadSiblingRanges = () => {
    if (!gameId || scheduled) { setSiblingRanges([]); return; }
    getAnnouncements(gameId)
      .then((rows) => {
        const selfId = initialData?.id;
        setSiblingRanges(
          (Array.isArray(rows) ? rows : [])
            // An announcement with no usable dates can't clash with anything (and can't be widened to a week/month).
            .filter((a) => a.id !== selfId
              && ISO_DATE.test(String(a.startDate).slice(0, 10)) && ISO_DATE.test(String(a.endDate).slice(0, 10)))
            .map((a) => ({
              id: a.id,
              name: a.name,
              startStr: String(a.startDate).slice(0, 10),
              endStr: String(a.endDate).slice(0, 10),
            })),
        );
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('AnnouncementFormModal: failed to load sibling announcements for date-clash checks', err);
        setSiblingRanges([]);
      });
  };

  const loadCatalog = () => {
    Promise.all([getItemPicker().catch(() => []), getPrizeCatalog().catch(() => [])])
      .then(([fullCatalog, prizeCatalog]) => {
        const merged = new Map();
        (Array.isArray(fullCatalog) ? fullCatalog : []).forEach((it) => merged.set(it.itemId, it));
        (Array.isArray(prizeCatalog) ? prizeCatalog : []).forEach((it) => merged.set(it.itemId, it));
        const withoutAvatars = [...merged.values()].filter((it) => it.itemTypeId !== AVATAR_ITEM_TYPE_ID);
        setCatalog(withoutAvatars.sort((a, b) => catalogItemLabel(a).localeCompare(catalogItemLabel(b))));
      });
  };

  useEffect(() => {
    if (!open) return;
    loadCatalog();
    loadSiblingRanges();
    if (scheduled && gameId) {
      setSchedulesLoading(true);
      getAvailableSchedules(gameId, initialData?.id ?? null)
        .then((rows) => setSchedules(Array.isArray(rows) ? rows : []))
        .catch(() => setSchedules([]))
        .finally(() => setSchedulesLoading(false));
    } else {
      setSchedules([]);
    }
    getItemTypes().then((data) => setItemTypes(Array.isArray(data) ? data : [])).catch(() => {});
    if (initialData) {
      setName(initialData.name ?? '');
      setScheduleIds(announcementQuizzes(initialData).map((x) => x.id).filter((id) => id != null));
      // The API returns AnnouncementType as its C# enum name ("Weekly"/"Monthly"/"Custom",
      // PascalCase) while every comparison in this file uses the lowercase literals from
      // ANNOUNCEMENT_TYPES (what gets sent back on save) — lowercase it here so the period
      // toggle, the resolved Start/End Date fields, and resolvePeriod() all match it.
      setAnnouncementType(String(initialData.announcementType ?? 'weekly').toLowerCase());
      setStartDate(initialData.startDate ? String(initialData.startDate).slice(0, 10) : '');
      setEndDate(initialData.endDate ? String(initialData.endDate).slice(0, 10) : '');
      // No original reference date is stored (only the resolved start/end) — the period's
      // own start date is always inside that same period, so it's a correct default to show
      // and, if left untouched, recomputes the identical range.
      setReferenceDate(initialData.startDate ? String(initialData.startDate).slice(0, 10) : todayIso());
      setAnnouncementDate(initialData.announcementDate ? String(initialData.announcementDate).slice(0, 10) : '');
      setTopCount(initialData.topCount != null ? String(initialData.topCount) : String(DEFAULT_TOP_COUNT));
      if (gameId && initialData.id) {
        getPrizePackItems(gameId, initialData.id)
          .then((rows) => setPrizePackItemIds((rows || []).map((r) => r.itemId)))
          .catch(() => setPrizePackItemIds([]));
      } else {
        setPrizePackItemIds([]);
      }
    } else {
      setName('');
      setScheduleIds([]);
      setAnnouncementType('weekly');
      setStartDate('');
      setEndDate('');
      setReferenceDate(todayIso());
      setAnnouncementDate('');
      setTopCount(String(DEFAULT_TOP_COUNT));
      setPrizePackItemIds([]);
    }
  }, [open, initialData, gameId, scheduled]);

  const catalogById = useMemo(() => new Map(catalog.map((it) => [it.itemId, it])), [catalog]);
  const selectedIds = useMemo(() => new Set(prizePackItemIds), [prizePackItemIds]);

  // The quizzes that can be picked: the loaded ones plus the ones the announcement already has, so a saved quiz is
  // still shown and kept even when the available list no longer returns it.
  const scheduleOptions = useMemo(() => {
    const byId = new Map(schedules.map((x) => [x.id, x]));
    if (scheduled) announcementQuizzes(initialData).forEach((x) => { if (x.id != null && !byId.has(x.id)) byId.set(x.id, x); });
    // Earliest first (then the earlier end, then the name), so the list reads like a calendar.
    return [...byId.values()].sort((a, b) => isoDate(a.startDate).localeCompare(isoDate(b.startDate))
      || isoDate(a.endDate).localeCompare(isoDate(b.endDate))
      || String(a.name ?? '').localeCompare(String(b.name ?? '')));
  }, [schedules, initialData, scheduled]);
  const selectedSchedules = scheduleIds.map((id) => scheduleOptions.find((x) => x.id === id)).filter(Boolean);
  // Quiz: the dates are the earliest start and the latest end of the picked quizzes (the server computes the same).
  const quizDates = quizSpan(selectedSchedules);

  const resolvedPeriod = (!isCustom && !scheduled) ? resolvePeriod(announcementType, referenceDate) : null;

  // For Weekly/Monthly, Start/End Date are always the server-resolved boundaries — shown as
  // real (disabled) date fields rather than a separate preview box, so the admin sees the
  // exact same field shape Custom uses, just non-editable since the server ignores manual
  // edits here anyway. For Custom, they're the actual editable inputs.
  // Quiz: a read-only preview of the picked quizzes' span (see quizDates); the server is authoritative.
  const effectiveStartDate = scheduled ? (quizDates?.start ?? '') : (isCustom ? startDate : (resolvedPeriod ? toIso(resolvedPeriod.start) : ''));
  const effectiveEndDate = scheduled ? (quizDates?.end ?? '') : (isCustom ? endDate : (resolvedPeriod ? toIso(resolvedPeriod.end) : ''));

  // Only Custom (Penalty) lets the admin pick the dates.
  const datesEditable = isCustom;

  const rangeError = datesEditable && Boolean(startDate && endDate) && startDate > endDate;

  // Custom: blocks individual days in the calendar (see disabledRanges below), but that alone
  // doesn't stop picking two UNBLOCKED endpoints that still straddle a blocked range in
  // between (e.g. start before it, end after it) — so the effective range is re-checked here
  // regardless. Weekly/Monthly block whole weeks/months in their picker (occupiedPeriodRanges below), so
  // this is their safety net rather than the first signal. Not for a Quiz (its announcements may overlap).
  const dateClash = useMemo(() => {
    if (scheduled || !effectiveStartDate || !effectiveEndDate || rangeError) return null;
    return siblingRanges.find((r) => rangesOverlap(effectiveStartDate, effectiveEndDate, r.startStr, r.endStr)) || null;
  }, [scheduled, effectiveStartDate, effectiveEndDate, rangeError, siblingRanges]);

  // Custom: the Start/End pickers block the days other announcements already cover.
  const disabledDateRanges = useMemo(
    () => siblingRanges.map((r) => ({ startStr: r.startStr, endStr: r.endStr })),
    [siblingRanges],
  );

  // Weekly/Monthly: one day picks a whole week/month, so every day of a week/month that touches another announcement
  // is blocked (red, not clickable) in the "which week/month" picker. Each announcement's range is widened to whole
  // weeks/months with the same formula that resolves the period (resolvePeriod, mirrored from the server), so what is
  // red here is exactly what would clash on save. Editing leaves the announcement itself out (siblingRanges does).
  const occupiedPeriodRanges = useMemo(() => {
    if (scheduled || isCustom) return [];
    return siblingRanges.flatMap((r) => {
      const first = resolvePeriod(announcementType, r.startStr);
      const last = resolvePeriod(announcementType, r.endStr);
      return first && last ? [{ startStr: toIso(first.start), endStr: toIso(last.end) }] : [];
    });
  }, [scheduled, isCustom, announcementType, siblingRanges]);

  // A NEW Weekly/Monthly announcement starts on today's week/month; when that one is already taken (or the admin
  // switches to a type whose current period is), move to the first free week/month after it instead of opening on a
  // blocked choice. Edits keep their saved period.
  useEffect(() => {
    if (!open || isEdit || scheduled || isCustom || siblingRanges.length === 0) return;
    const taken = (ref) => {
      const p = resolvePeriod(announcementType, ref);
      return Boolean(p) && siblingRanges.some((r) => rangesOverlap(toIso(p.start), toIso(p.end), r.startStr, r.endStr));
    };
    if (!taken(referenceDate)) return;
    let candidate = referenceDate;
    for (let i = 0; i < 600; i += 1) {
      const p = resolvePeriod(announcementType, candidate);
      candidate = toIso(new Date(p.end.getTime() + MS_PER_DAY)); // the first day of the next week/month
      if (!taken(candidate)) { setReferenceDate(candidate); return; }
    }
  }, [open, isEdit, scheduled, isCustom, announcementType, siblingRanges, referenceDate]);

  const topCountNum = Number(topCount);
  const isTopCountValid = topCount !== '' && Number.isInteger(topCountNum) && topCountNum > 0 && topCountNum <= MAX_TOP_COUNT;
  const isNameValid = name.trim().length > 0;
  const isDateValid = scheduled
    ? selectedSchedules.length > 0
    : isCustom
    ? Boolean(startDate) && Boolean(endDate) && !rangeError && !dateClash
    : Boolean(referenceDate) && !dateClash;
  // The announcement date is the day prizes are planned to be granted, so it can't be before the announcement ends.
  const announcementDateTooEarly = Boolean(announcementDate && effectiveEndDate && announcementDate < effectiveEndDate);
  const announcementDateError = announcementDateTooEarly
    ? `Announcement date must be on or after the end date (${fmtDateOnly(effectiveEndDate)}).`
    : null;
  const isAnnouncementDateValid = Boolean(announcementDate) && !announcementDateTooEarly;
  const isValid = isNameValid && isTopCountValid && isDateValid && isAnnouncementDateValid;

  const handleTogglePrizeItem = (itemId) => {
    setPrizePackItemIds((prev) => (
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    ));
  };

  const handleRemovePrizePackItem = (itemId) => {
    setPrizePackItemIds((prev) => prev.filter((id) => id !== itemId));
  };

  const handleCreatePriceConfirmed = () => {
    navigate('/items', { state: { activeTab: 'prizes' } });
  };

  // Changing Start Date always resets End Date — otherwise a new Start silently pairs with
  // a stale old End (which may now be much further away, or before the new Start entirely),
  // instead of the admin picking a deliberate new End for the new Start.
  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
    setEndDate('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) return;
    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        ...(scheduled
          ? { gameScheduleIds: scheduleIds }
          : {
            announcementType,
            ...(!isCustom && { referenceDate }),
            ...(isCustom ? { startDate, endDate } : {}),
          }),
        topCount: topCountNum,
        announcementDate,
        prizePackItems: prizePackItemIds.map((itemId) => ({ itemId })),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px', border: `1px solid ${C.border}`, overflow: 'hidden' } }}
    >
      {/* Matches CreateBoothModal's header convention (navy bar, white title/close) — this
          modal previously used a plain white header, inconsistent with the rest of the CMS. */}
      <DialogTitle sx={{ bgcolor: C.navy, color: 'white', pr: 6, py: 2.5 }}>
        <Typography variant="h6" fontWeight={700} component="span" fontSize={15}>
          <LocalText>{isEdit ? 'Edit Announcement' : 'Create Announcement'}</LocalText>
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Close"
          sx={{ position: 'absolute', right: 10, top: 14, color: 'white' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3, ...thinScrollbarSx }}>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography fontSize={11} sx={{ color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.5 }}>
              <LocalText>Name</LocalText>
            </Typography>
            <TextField
              size="small"
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. September Weekly Leaderboard Prize"
              inputProps={{ maxLength: 200, 'aria-label': 'Announcement name' }}
              sx={inputSx}
              error={name !== '' && !isNameValid}
            />
          </Box>

          {scheduled ? (
            <Box>
              <Typography fontSize={11} sx={{ color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.5 }}>
                <LocalText>Quizzes *</LocalText>
              </Typography>
              <FormControl size="small" fullWidth error={!hasWinners && !schedulesLoading && selectedSchedules.length === 0}>
                <Select
                  multiple
                  value={scheduleIds}
                  onChange={(e) => setScheduleIds(e.target.value)}
                  displayEmpty
                  disabled={hasWinners || schedulesLoading}
                  inputProps={{ 'aria-label': 'Quizzes' }}
                  sx={inputSx}
                  MenuProps={quizMenuProps}
                  renderValue={() => {
                    if (selectedSchedules.length === 0) return <Typography sx={{ color: '#94a3b8', fontSize: 13 }}><LocalText>Select one or more quizzes</LocalText></Typography>;
                    return (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selectedSchedules.map((x) => (
                          <Chip key={x.id} size="small" label={x.name} sx={{ fontSize: 12, bgcolor: '#eff6ff', color: C.navy, fontWeight: 600 }} />
                        ))}
                      </Box>
                    );
                  }}
                >
                  {scheduleOptions.map((x) => (
                    <MenuItem key={x.id} value={x.id} sx={quizOptionSx}>
                      {/* The row itself takes the click; the box only shows the state. */}
                      <Checkbox
                        checked={scheduleIds.includes(x.id)}
                        size="small"
                        disableRipple
                        tabIndex={-1}
                        sx={{ p: 0.25, pointerEvents: 'none', color: C.border, '&.Mui-checked': { color: C.navy } }}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography component="span" sx={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: C.navy }}>
                          {x.name}
                        </Typography>
                        <Typography component="span" sx={{ display: 'block', fontSize: 11.5, color: C.muted }}>
                          {formatPeriodRange(x.startDate, x.endDate)}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {!schedulesLoading && selectedSchedules.length === 0 && (
                  <FormHelperText>
                    <LocalText>
                      {scheduleOptions.length === 0
                        ? 'No approved quiz is available. Quizzes that are already in another announcement are not listed.'
                        : 'Select the quizzes this announcement is for.'}
                    </LocalText>
                  </FormHelperText>
                )}
                {hasWinners && (
                  <FormHelperText><LocalText>Winners were already granted for this announcement, so its quizzes can't be changed.</LocalText></FormHelperText>
                )}
              </FormControl>
            </Box>
          ) : (
            <>
          <Box>
            <Typography fontSize={11} sx={{ color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.5 }}>
              <LocalText>Period</LocalText>
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={announcementType}
              onChange={(_e, next) => { if (next !== null) setAnnouncementType(next); }}
              sx={toggleGroupSx}
              aria-label="announcement period"
            >
              {ANNOUNCEMENT_TYPES.map((t) => (
                <ToggleButton key={t.value} value={t.value} aria-label={t.label}>
                  <LocalText>{t.label}</LocalText>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {!isCustom && (
            <Box>
              <Typography fontSize={11} sx={{ color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.5 }}>
                <LocalText>{announcementType === 'weekly' ? 'Which week? (pick any day in it)' : 'Which month? (pick any day in it)'}</LocalText>
              </Typography>
              <CustomDatePicker
                value={referenceDate}
                onChange={(e) => setReferenceDate(e.target.value)}
                placeholder="Select a date"
                enableYearNav
                fullWidth
                highlightWeek={announcementType === 'weekly'}
                highlightMonth={announcementType === 'monthly'}
                disabledRanges={occupiedPeriodRanges}
              />
            </Box>
          )}
            </>
          )}

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Box sx={{ flex: 1 }}>
              <Typography fontSize={11} sx={{ color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.5 }}>
                <LocalText>Start Date</LocalText>
              </Typography>
              <CustomDatePicker
                value={effectiveStartDate}
                max={datesEditable ? (endDate || undefined) : undefined}
                onChange={handleStartDateChange}
                placeholder="Start date"
                disabled={!datesEditable}
                enableYearNav
                fullWidth
                highlightRange={datesEditable}
                rangeAnchor={datesEditable ? (endDate || null) : null}
                disabledRanges={datesEditable ? disabledDateRanges : []}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography fontSize={11} sx={{ color: rangeError ? '#ef4444' : C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.5 }}>
                <LocalText>End Date</LocalText>
              </Typography>
              <CustomDatePicker
                value={effectiveEndDate}
                min={datesEditable ? (startDate || undefined) : undefined}
                onChange={(e) => setEndDate(e.target.value)}
                placeholder="End date"
                isError={rangeError}
                disabled={!datesEditable}
                enableYearNav
                fullWidth
                highlightRange={datesEditable}
                rangeAnchor={datesEditable ? (startDate || null) : null}
                disabledRanges={datesEditable ? disabledDateRanges : []}
              />
            </Box>
          </Box>
          {scheduled && (
            <Typography sx={{ fontSize: 11, color: C.muted, mt: -1 }}>
              <LocalText>Start and End Date are set automatically: the earliest start and the latest end of the selected quizzes.</LocalText>
            </Typography>
          )}
          {rangeError && (
            <Typography sx={{ fontSize: 11, color: '#ef4444', mt: -1 }}>
              <LocalText>Start date must be on or before the end date.</LocalText>
            </Typography>
          )}
          {!rangeError && dateClash && (
            <Typography sx={{ fontSize: 11, color: '#ef4444', mt: -1 }}>
              <LocalText>{`This period overlaps "${dateClash.name}" (${dateClash.startStr} to ${dateClash.endStr}). Choose a non-overlapping period.`}</LocalText>
            </Typography>
          )}

          <Box>
            <Typography fontSize={11} sx={{ color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.5 }}>
              <LocalText>Announcement Date (planned prize-grant day, on or after the end date)</LocalText>
            </Typography>
            <CustomDatePicker
              value={announcementDate}
              min={effectiveEndDate || undefined}
              onChange={(e) => setAnnouncementDate(e.target.value)}
              placeholder="Select a date"
              isError={announcementDateTooEarly}
              enableYearNav
              fullWidth
            />
            {announcementDateError && (
              <Typography role="alert" sx={{ fontSize: 11, color: '#ef4444', mt: 0.5 }}>
                <LocalText>{announcementDateError}</LocalText>
              </Typography>
            )}
          </Box>

          <Box>
            <Typography fontSize={11} sx={{ color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.5 }}>
              <LocalText>Top Count (eligible players)</LocalText>
            </Typography>
            <TextField
              size="small"
              type="number"
              fullWidth
              value={topCount}
              onChange={(e) => setTopCount(e.target.value)}
              inputProps={{ min: 1, max: MAX_TOP_COUNT, 'aria-label': 'Top count' }}
              sx={inputSx}
              error={topCount !== '' && !isTopCountValid}
            />
            {topCount !== '' && !isTopCountValid && (
              <Typography role="alert" sx={{ fontSize: 11, color: '#ef4444', mt: 0.5 }}>
                <LocalText>{`Enter a whole number from 1 to ${MAX_TOP_COUNT}.`}</LocalText>
              </Typography>
            )}
          </Box>

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
              <Typography fontSize={11} sx={{ color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                <LocalText>Prize(s)</LocalText>
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setPickerOpen(true)}
                sx={{ ...btnBase, borderColor: C.border, color: C.navy, '&:hover': { borderColor: C.navy, bgcolor: '#eff6ff' } }}
              >
                <LocalText>Select Item</LocalText>
              </Button>
            </Box>

            {prizePackItemIds.length === 0 ? (
              <Typography fontSize={12} sx={{ color: '#94a3b8' }}>
                <LocalText>No prize items selected yet.</LocalText>
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {prizePackItemIds.map((itemId) => {
                  const item = catalogById.get(itemId);
                  const imageUrl = catalogItemImage(item);
                  return (
                    <Box
                      key={itemId}
                      sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
                        px: 1.25, py: 0.5, bgcolor: '#f8fafc', borderRadius: '8px',
                      }}
                    >
                      <Typography fontSize={12} sx={{ color: '#1e293b', flex: 1 }}>
                        {catalogItemLabel(item) || `Item #${itemId}`}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => setPreviewUrl(imageUrl)}
                        disabled={!imageUrl}
                        aria-label="View item image"
                      >
                        <VisibilityOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleRemovePrizePackItem(itemId)} aria-label="Remove">
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>

        </Box>
      </DialogContent>

      {/* Matches CreateBoothModal's footer convention (bgcolor: 'grey.50', separated from
          content by DialogContent's own bottom edge) — this modal previously had Cancel/Save
          floating inline in the same white scroll area as the form fields, with no visual
          separation at all. Right-aligned (not Booth's centered pair) — matches every other
          two-button footer already in this feature (AnnouncementDetailModal, etc.). */}
      <DialogActions sx={{ justifyContent: 'flex-end', gap: 1.5, px: 3, py: 2, bgcolor: '#f8fafc', borderTop: `1px solid ${C.border}` }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{ ...btnBase, borderColor: C.border, color: C.navy, '&:hover': { borderColor: C.navy, bgcolor: '#eff6ff' } }}
        >
          <LocalText>Cancel</LocalText>
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isValid || saving}
          sx={{ ...btnBase, ...ctaNavy }}
        >
          <LocalText>{saving ? 'Saving…' : 'Save'}</LocalText>
        </Button>
      </DialogActions>

      <PrizeItemPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        catalog={catalog}
        itemTypes={itemTypes}
        selectedIds={selectedIds}
        onToggle={handleTogglePrizeItem}
        onCreatePriceConfirmed={handleCreatePriceConfirmed}
      />

      <MediaPreviewOverlay
        open={Boolean(previewUrl)}
        onClose={() => setPreviewUrl(null)}
        type="image"
        url={previewUrl}
      />
    </Dialog>
  );
}
